import { useQuery, useMutation, gql } from '@apollo/client';
import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { GET_TAGS as GET_POSSIBLE_TAGS } from '../../../queries/tags';
import { TagsPicker } from '../../../components/Protected/TagsPicker';
import { MediaTile } from '../../../components/MediaTile';
import { useGalleryGate } from '../../../hooks/galleryAccess';
import { PATHS } from '../../../paths';
import { MAX_IMAGE_CAPTION_LENGTH } from '../../../utils/media';
import { useState } from 'react';
import { Button, Message, Dropdown, TextArea } from 'semantic-ui-react'

const GET_SINGLE_IMAGE = gql`
  query Image($imageId: Int!) {
    image(id: $imageId) {
      id
      url
      contentType
      durationSeconds
      caption
      galleryId
      gallery {
        id
        name
        myAccessLevel
      }
      tags {
        id
        groupId
        name
      }
      subjects {
        id
        user {
          name
        }
      }
    }
  }
`;

const SET_IMAGE_CAPTION = gql`
  mutation SetImageCaption($imageId: Int!, $caption: String) {
    setImageCaption(imageId: $imageId, caption: $caption) { id caption }
  }
`;

const GET_MEMBERS_I_CAN_TAG = gql`
  query MembersICanTag {
    membersICanTag {
      id
      user {
        name
      }
    }
  }
`;

const UPDATE_IMAGE_TAGS = gql`
  mutation UpdateImageTags($imageId: Int!, $tagIds: [Int!]!) {
    updateImageTags(imageId: $imageId, tagIds: $tagIds) {
      id
      name
    }
  }
`;

const UPDATE_MEMBER_IMAGES = gql`
  mutation UpdateMemberImages($imageId: Int!, $membershipIds: [Int!]!) {
    updateMemberImages(imageId: $imageId, membershipIds: $membershipIds) {
      id
      subjects {
        id
        user {
          name
        }
      }
    }
  }
`;

const GET_GALLERIES = gql`
  query Galleries {
    galleries {
      id
      name
      myAccessLevel
    }
  }
`;

const MOVE_IMAGES_TO_GALLERY = gql`
  mutation MoveImagesToGallery($imageIds: [Int!]!, $galleryId: Int!) {
    moveImagesToGallery(imageIds: $imageIds, galleryId: $galleryId)
  }
`;

/** Levels that allow putting an image *into* a gallery. */
const CAN_RECEIVE = ['CONTRIBUTE', 'EDIT', 'MANAGE'];
/** Levels that allow taking an image *out* of one — a stricter bar, and deliberately so. */
const CAN_RELEASE = ['EDIT', 'MANAGE'];

const ButtonStyles = {
    marginBottom: '20px',
    padding: '8px 16px',
    cursor: 'pointer',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
}

// gallery/1/tags?nextImageIds=2,3,4 so when i am done with this it will go to the next image
// if there are nextImages then we need to add a next button that jumps to the next image and if
// there are none just say done and take us back to however you go to this page

function TagImage() {
    const { imageId } = useParams<{ imageId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { loading: gateLoading, hasGalleryAccess } = useGalleryGate();

    let nextImageIds: number[] = []

    const idNum = Number(imageId);
    const navigate = useNavigate();

    const {
        data: imageData,
        loading: imageLoading,
        refetch: refetchImage,
    } = useQuery(GET_SINGLE_IMAGE, {
        variables: { imageId: idNum },
    });

    const {
        data: allTagsData,
        loading: tagsLoading,
        error: tagsError,
    } = useQuery(GET_POSSIBLE_TAGS, {
        variables: {
            showPeopleTags: false,
            showCourseTags: true,
        },
    });

    const {
        data: membersData,
        loading: membersLoading,
        error: membersError,
    } = useQuery(GET_MEMBERS_I_CAN_TAG);

    const [updateImageTags] = useMutation(UPDATE_IMAGE_TAGS);
    const [setImageCaption, { loading: savingCaption }] = useMutation(SET_IMAGE_CAPTION);
    const [updateMemberImages] = useMutation(UPDATE_MEMBER_IMAGES);

    const { data: galleriesData } = useQuery(GET_GALLERIES);
    const [moveImagesToGallery] = useMutation(MOVE_IMAGES_TO_GALLERY);
    const [movingTo, setMovingTo] = useState<number | null>(null);
    const [moveError, setMoveError] = useState<string | null>(null);

    /**
     * The caption, held locally while it is being typed.
     *
     * `null` means "not being edited" and the stored value is shown; anything else is an
     * unsaved draft. Seeding it from the query on every render instead would overwrite what
     * somebody was halfway through typing each time the image refetched.
     */
    const [captionDraft, setCaptionDraft] = useState<string | null>(null);
    const [captionError, setCaptionError] = useState<string | null>(null);
    const [captionSaved, setCaptionSaved] = useState(false);

    const saveCaption = async (next: string) => {
        setCaptionError(null);
        setCaptionSaved(false);
        try {
            await setImageCaption({ variables: { imageId: idNum, caption: next.trim() || null } });
            await refetchImage();
            setCaptionDraft(null);
            setCaptionSaved(true);
        } catch (err) {
            // The server enforces the 300-character limit and the CONTRIBUTE gate, and its
            // message says which — more use than a generic failure.
            setCaptionError(err instanceof Error ? err.message : 'That did not save');
        }
    };

    const moveToGallery = async (galleryId: number) => {
        setMovingTo(galleryId);
        setMoveError(null);
        try {
            await moveImagesToGallery({ variables: { imageIds: [idNum], galleryId } });
            await refetchImage();
        } catch (err) {
            // The server checks both ends of the move, and the rules are not guessable from
            // the dropdown alone — its message is the useful one.
            setMoveError(err instanceof Error ? err.message : 'That did not work');
        } finally {
            setMovingTo(null);
        }
    };

    const list = searchParams.get('nextImageIds');
    if (list != null) {
        const items = list.split(',')
        nextImageIds = items.map(Number)
    }

    // Phase 5.9 — anyone who can reach a gallery can be here; everyone else goes back to
    // classes. Must come before the "Image not found" branch below: the backend returns null
    // for an image whose gallery you cannot reach, so without this they would land on that
    // message instead of being redirected.
    // Wait for the query so a legitimate user isn't bounced during the initial load.
    if (!gateLoading && !hasGalleryAccess) {
        return <Navigate to={PATHS.CLASSES} replace />;
    }

    if (imageLoading) return <div style={{ padding: '20px' }}>Loading Image...</div>;
    if (!imageData?.image) return <div style={{ padding: '20px' }}>Image not found</div>;

    const currentSubjects = imageData.image.subjects.map((s: any) => ({
        id: s.id,
        name: s.user?.name || 'Unknown User',
    }));

    const availableMembers = {
        tags: membersData?.membersICanTag?.map((m: any) => ({
            id: m.id,
            name: m.user?.name || 'Unknown Member',
        })) || [],
    };

    // Taking an image *out* of a gallery is a stricter bar than putting one in: it removes
    // it from everyone who could see it there. CONTRIBUTE uploads; EDIT rearranges.
    const canMoveOut = CAN_RELEASE.includes(imageData.image.gallery?.myAccessLevel);

    const scopes = imageData.image.tags.filter((t: any) => t.groupId === 2);

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px' }}>
            <button
                onClick={() => navigate(-1)}
                style={ButtonStyles}
            >
                ← Back
            </button>

            <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>
                <div
                    style={{
                        flex: '2',
                        textAlign: 'center',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        padding: '10px',
                    }}
                >
                    <MediaTile
                        url={imageData.image.url}
                        contentType={imageData.image.contentType}
                        durationSeconds={imageData.image.durationSeconds}
                        alt="Target"
                        fluid
                        controls
                        style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            borderRadius: '4px',
                        }}
                    />
                </div>

                <div style={{ flex: '1', minWidth: '300px' }}>
                    <h3 style={{ marginTop: 0 }}>Image Management</h3>

                    {/* Which gallery this image lives in. It sits first because it is the
                        coarsest control on the page: the gallery decides who can see the
                        image at all, and the tags below only matter within it. */}
                    <div style={{ marginBottom: '25px' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Gallery</p>
                        <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '10px' }}>
                            Moving this image changes who can see it.
                        </p>

                        <Dropdown
                            selection
                            fluid
                            loading={movingTo != null}
                            disabled={movingTo != null || !canMoveOut}
                            value={imageData.image.gallery?.id}
                            options={(galleriesData?.galleries ?? [])
                                .filter((g: any) => CAN_RECEIVE.includes(g.myAccessLevel)
                                    || g.id === imageData.image.gallery?.id)
                                .map((g: any) => ({
                                    key: g.id,
                                    value: g.id,
                                    text: g.name,
                                    // A gallery you can see but not add to would fail on the
                                    // server; saying so here beats a refusal after the click.
                                    disabled: !CAN_RECEIVE.includes(g.myAccessLevel),
                                }))}
                            onChange={(_, { value }) => {
                                const next = Number(value);
                                if (next !== imageData.image.gallery?.id) {
                                    moveToGallery(next);
                                }
                            }}
                        />

                        {!canMoveOut && (
                            <p style={{ color: '#999', fontSize: '0.8rem', marginTop: '8px' }}>
                                You can add images to{' '}
                                <strong>{imageData.image.gallery?.name ?? 'this gallery'}</strong>{' '}
                                but not move them out of it. A gallery manager can.
                            </p>
                        )}

                        {moveError && <Message error size="tiny" content={moveError} style={{ marginTop: '10px' }} />}
                    </div>

                    {/* The caption, above the tags because it describes the picture itself
                        where a tag says which category it belongs to — and because it is
                        written at upload, so this is where a mistake gets corrected.

                        Same gate as tagging (CONTRIBUTE on the gallery, enforced by
                        `setImageCaption`), so anybody who can reach the tag controls below can
                        reach this one. */}
                    <div style={{ marginBottom: '25px' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Caption</p>
                        <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '10px' }}>
                            What is happening in this picture. Shown under it in the gallery.
                        </p>

                        <TextArea
                            rows={3}
                            maxLength={MAX_IMAGE_CAPTION_LENGTH}
                            placeholder="e.g. Level 2 sewing class, Khal Par"
                            value={captionDraft ?? imageData.image.caption ?? ''}
                            disabled={savingCaption}
                            onChange={(_, { value }) => {
                                setCaptionSaved(false);
                                setCaptionDraft(String(value ?? ''));
                            }}
                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontFamily: 'inherit' }}
                        />

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                            <Button
                                size="tiny"
                                primary
                                loading={savingCaption}
                                // Nothing to save until it differs from what is stored, so the
                                // button cannot be used to write the same value back.
                                disabled={savingCaption || captionDraft === null
                                    || captionDraft.trim() === (imageData.image.caption ?? '')}
                                onClick={() => saveCaption(captionDraft ?? '')}
                            >
                                Save caption
                            </Button>
                            {captionDraft !== null && !savingCaption && (
                                <Button
                                    size="tiny"
                                    basic
                                    onClick={() => { setCaptionDraft(null); setCaptionError(null); }}
                                >
                                    Cancel
                                </Button>
                            )}
                            <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#999' }}>
                                {(captionDraft ?? imageData.image.caption ?? '').length} / {MAX_IMAGE_CAPTION_LENGTH}
                            </span>
                        </div>

                        {captionSaved && <Message positive size="tiny" content="Caption saved." style={{ marginTop: '10px' }} />}
                        {captionError && <Message error size="tiny" content={captionError} style={{ marginTop: '10px' }} />}
                    </div>

                    <div style={{ marginBottom: '25px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '6px', border: '1px solid #eee' }}>
                        <p style={{ fontWeight: 'bold', margin: '0 0 10px 0', fontSize: '0.9rem', color: '#555' }}>Communities with visibility</p>
                        {scopes.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#333' }}>
                                {scopes.map((scope: any) => (
                                    <li key={scope.id} style={{ marginBottom: '4px' }}>{scope.name}</li>
                                ))}
                            </ul>
                        ) : (
                            <span style={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>No scopes selected</span>
                        )}
                    </div>

                    {scopes.length == 0 && (
                        <><Message>
                            <Message.Header>No scopes detected</Message.Header>
                            <p>You must add this images to at least one community</p>
                        </Message></>
                    )}

                    <div style={{ marginBottom: '30px' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>General Tags</p>
                        <TagsPicker
                            tags={imageData.image.tags}
                            refetchParent={refetchImage}
                            loading={tagsLoading}
                            error={tagsError}
                            data={allTagsData}
                            updateTags={updateImageTags}
                            payload={{ imageId: idNum }}
                        />
                    </div>

                    <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '30px 0' }} />

                    <div style={{ marginBottom: '20px' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Associated Members</p>
                        <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '15px' }}>
                            Tag people that appear in this image.
                        </p>

                        <TagsPicker
                            tags={currentSubjects}
                            refetchParent={refetchImage}
                            loading={membersLoading}
                            error={membersError}
                            data={availableMembers}
                            updateTags={(options: any) => {
                                const ids = options.variables?.tagIds || [];
                                return updateMemberImages({
                                    variables: {
                                        imageId: idNum,
                                        membershipIds: ids,
                                    },
                                });
                            }}
                            payload={{ imageId: idNum }}
                        />
                    </div>
                </div>
            </div>
             <div style={{ float: 'right' }}>
            {nextImageIds.length != 0 &&
                <button
                    disabled={scopes.length == 0}
                    style={ButtonStyles}
                    onClick={() => {
                        let path = '';
                        if (nextImageIds.length > 0) {
                            path = `/gallery/${nextImageIds[0]}/tags`
                        }

                        if (nextImageIds.length > 1) {
                            nextImageIds.shift()
                            path = `${path}?nextImageIds=${nextImageIds.join(',')}`
                        }
                        navigate(path)

                    }}
                >
                    Done - next image
                </button>}
                {nextImageIds.length == 0 && <button
                    onClick={() => navigate('/gallery')}
                    style={ButtonStyles}
                >
                    Go to gallery
                </button>}
            </div>
        </div>
    );
}

export { TagImage };
